import React, { useEffect, useState } from 'react';
import { formatearCuit, cuitValido, soloDigitos } from '../utils/cuit';

const FUNCTIONS_URL = process.env.REACT_APP_FUNCTIONS_URL || 'https://us-central1-transferisok.cloudfunctions.net';

// Campo CUIT del registro. Con los 11 números busca el titular en el padrón de ARCA
// (functions/src/arca/registro.js) y lo muestra para que la persona confirme que es
// el suyo. El resultado lo guarda Negocio.js en `titular`, con uno de estos estados:
//   vacio | invalido | buscando | encontrado | no_encontrado | sin_respuesta
// sin_respuesta es que ARCA no contestó: se puede seguir igual, sin el nombre.

async function buscarTitular(cuit, signal) {
    const r = await fetch(`${FUNCTIONS_URL}/consultarCuit?cuit=${cuit}`, { signal });
    if (r.status === 400) return { estado: 'invalido' };
    if (!r.ok) return { estado: 'sin_respuesta' };
    const d = await r.json();
    if (!d.encontrado) return { estado: 'no_encontrado', inactivo: !!d.inactivo };
    return { estado: 'encontrado', nombre: d.nombre, tipo: d.tipo };
}

const estiloAviso = (color) => ({ marginTop: '8px', fontSize: '13px', lineHeight: 1.45, color });

function CampoCuit({ value, onChange, titular, onTitular, onFocus, onBlur }) {
    const [tocado, setTocado] = useState(false);

    useEffect(() => {
        if (value.length < 11) {
            onTitular({ estado: 'vacio' });
            return undefined;
        }
        if (!cuitValido(value)) {
            onTitular({ estado: 'invalido' });
            return undefined;
        }

        let vigente = true;
        const controller = new AbortController();
        // ARCA suele contestar en 1 o 2 segundos. Si no contesta, se sigue sin el nombre.
        const tope = setTimeout(() => controller.abort(), 15000);
        onTitular({ estado: 'buscando' });
        buscarTitular(value, controller.signal)
            .then((r) => vigente && onTitular(r))
            .catch(() => vigente && onTitular({ estado: 'sin_respuesta' }));
        return () => {
            vigente = false;
            clearTimeout(tope);
            controller.abort();
        };
    }, [value, onTitular]);

    let aviso = null;
    if (titular.estado === 'vacio' && tocado && value.length > 0) {
        aviso = <div style={estiloAviso('#b45309')}>El CUIT tiene 11 números.</div>;
    } else if (titular.estado === 'invalido') {
        aviso = <div style={estiloAviso('#dc2626')}>Ese CUIT no es válido. Revisá los números.</div>;
    } else if (titular.estado === 'buscando') {
        aviso = (
            <div style={{ ...estiloAviso('#64748b'), display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span className="campo-cuit-spinner" />
                Buscando el titular en ARCA…
            </div>
        );
    } else if (titular.estado === 'no_encontrado') {
        aviso = (
            <div style={estiloAviso('#dc2626')}>
                {titular.inactivo
                    ? 'Ese CUIT figura inactivo en ARCA. Revisá el número.'
                    : 'No encontramos ese CUIT en ARCA. Revisá el número.'}
            </div>
        );
    } else if (titular.estado === 'sin_respuesta') {
        aviso = <div style={estiloAviso('#64748b')}>No pudimos consultar ARCA en este momento. Podés seguir igual.</div>;
    } else if (titular.estado === 'encontrado') {
        aviso = (
            <div style={{
                marginTop: '10px',
                padding: '12px 14px',
                borderRadius: '10px',
                backgroundColor: '#f0fdf4',
                border: '1px solid #bbf7d0'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <span aria-hidden="true" style={{
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        backgroundColor: '#16a34a',
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                    }}>✓</span>
                    <span style={{ fontWeight: 700, fontSize: '15px', color: '#001c4d' }}>
                        {titular.nombre || 'CUIT encontrado en ARCA'}
                    </span>
                    <span style={{
                        fontSize: '12px',
                        fontWeight: 600,
                        padding: '2px 8px',
                        borderRadius: '999px',
                        backgroundColor: '#dcfce7',
                        color: '#166534'
                    }}>
                        {titular.tipo === 'empresa' ? 'Empresa' : 'Persona'}
                    </span>
                </div>
                <div style={{ marginTop: '6px', fontSize: '13px', lineHeight: 1.45, color: '#475569' }}>
                    ¿Es correcto? Tiene que ser la persona o empresa titular del negocio.
                    Si no lo es, revisá el número de CUIT.
                </div>
            </div>
        );
    }

    return (
        <div className="registro-campo">
            <label htmlFor="negocio-cuit" className="registro-label">CUIT</label>
            <input
                id="negocio-cuit"
                type="text"
                inputMode="numeric"
                autoComplete="off"
                name="DNI_CUIT"
                value={formatearCuit(value)}
                onChange={(e) => onChange(soloDigitos(e.target.value).slice(0, 11))}
                onFocus={onFocus}
                onBlur={(e) => {
                    setTocado(true);
                    onBlur(e);
                }}
                className="registro-input"
                placeholder="20-12345678-9"
                required
                autoFocus
            />
            {aviso}
            <style>{`
                .campo-cuit-spinner {
                    width: 14px;
                    height: 14px;
                    border: 2px solid #cbd5e1;
                    border-top-color: #001c4d;
                    border-radius: 50%;
                    animation: campo-cuit-giro 0.8s linear infinite;
                    flex-shrink: 0;
                }
                @keyframes campo-cuit-giro {
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
}

export default CampoCuit;
